import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { musicAlbums, bundleImages } from '@/mocks/neurobaby';

function AudioPlayerModal({ album, onClose }: { album: typeof musicAlbums[0]; onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl font-bold text-[#8B6F5C]">
            {t('common.muestra_de')} {t(`music_albums.${album.id}.title`, album.title)}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-[#5C4A3A] hover:text-[#8B6F5C] transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <img src={album.image} alt={t(`music_albums.${album.id}.title`, album.title)} className="w-full h-48 object-cover rounded-xl mb-4" />
        <p className="font-sans text-sm text-[#3D2B1F] mb-4">
          {t(`music_albums.${album.id}.description`, album.description)}
        </p>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center bg-terracotta rounded-full text-white cursor-pointer hover:bg-brown transition-colors">
            <i className="ri-play-fill text-lg" />
          </div>
          <div className="flex-1 h-2 bg-sand rounded-full overflow-hidden">
            <div className="w-1/3 h-full bg-terracotta rounded-full" />
          </div>
          <span className="font-sans text-xs text-[#5C4A3A]">{t('common.muestra_30s')}</span>
        </div>
        <p className="font-sans text-xs text-[#5C4A3A] text-center">
          {t('common.reproduccion_muestra')}
        </p>
      </div>
    </div>
  );
}

export default function MusicLibrarySection() {
  const { t, i18n } = useTranslation();
  const [activeAlbum, setActiveAlbum] = useState<typeof musicAlbums[0] | null>(null);
  const [hoveredAlbum, setHoveredAlbum] = useState<number | null>(null);

  const totalPrice = musicAlbums.reduce((sum, a) => sum + a.price, 0);
  const bundlePrice = 49.99;
  const savings = totalPrice - bundlePrice;

  return (
    <>
      {activeAlbum && (
        <AudioPlayerModal
          album={activeAlbum}
          onClose={() => setActiveAlbum(null)}
        />
      )}

      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('home.music.subtitle')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('home.music.title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              {t('home.music.desc')}
            </p>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-10 mb-12 md:mb-16 border border-sand/40">
            <div className="w-8 h-8 flex items-center justify-center text-terracotta mb-4">
              <i className="ri-music-2-line text-2xl" />
            </div>
            <p className="font-sans text-base text-[#3D2B1F] leading-relaxed">
              {t('home.music.intro1')}
            </p>
          </div>

          {/* Próximamente + oblea neurociencia */}
          <div className="mb-12 md:mb-16">
            <div className="rounded-3xl bg-terracotta px-6 py-8 md:px-12 md:py-10 text-center">
              <span className="inline-flex items-center gap-2 bg-white/15 text-white font-sans text-xs font-semibold uppercase tracking-[0.25em] px-4 py-2 rounded-full mb-5">
                <i className="ri-time-line" />
                {t('home.music.proximamente_label')}
              </span>
              <p className="font-serif text-xl md:text-3xl font-bold text-white leading-snug mb-3 max-w-3xl mx-auto">
                {t('home.music.proximamente_title')}
              </p>
              <p className="font-sans text-sm md:text-base text-cream/90 max-w-2xl mx-auto">
                {t('home.music.proximamente_desc')}
              </p>
            </div>

            <div className="mt-4 rounded-3xl bg-[#AAB7A3]/20 border border-[#AAB7A3]/40 px-6 py-6 md:px-10 md:py-7 flex items-start gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#AAB7A3] text-white flex-shrink-0">
                <i className="ri-brain-line text-xl" />
              </div>
              <p className="font-sans text-sm md:text-base font-medium text-[#3D2B1F] leading-relaxed">
                {t('home.music.intro2')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-flow-col lg:grid-rows-3 gap-5 md:gap-6 mb-12 md:mb-16">
            {musicAlbums.map((album) => (
              <div
                key={album.id}
                className="bg-white rounded-2xl overflow-hidden border border-sand/40 transition-all duration-300 hover:border-terracotta/30 flex flex-col sm:flex-row"
                onMouseEnter={() => setHoveredAlbum(album.id)}
                onMouseLeave={() => setHoveredAlbum(null)}
              >
                <div className="relative w-full h-auto sm:h-auto sm:self-stretch sm:w-[130px] md:w-[150px] flex-shrink-0 overflow-hidden">
                  <img
                    src={i18n.language === 'en' ? (album.imageSquareEn || album.imageSquare || album.image) : (album.imageSquare || album.image)}
                    alt={t(`music_albums.${album.id}.title`, album.title)}
                    className="w-full h-auto object-contain transition-transform duration-500 sm:absolute sm:inset-0 sm:h-full sm:object-cover"
                    style={{
                      transform: hoveredAlbum === album.id ? 'scale(1.05)' : 'scale(1)',
                    }}
                  />
                </div>
                <div className="pl-3 pr-3 pt-3 md:pl-4 md:pr-4 md:pt-4 flex flex-col flex-grow justify-start min-w-0">
                  <span className="font-sans text-xs text-[#5C4A3A] mb-0.5 line-clamp-1">
                    {t(`music_albums.${album.id}.subtitle`, album.subtitle)}
                  </span>
                  <h3 className="font-serif text-lg font-bold text-[#8B6F5C] mb-1 leading-tight">
                    {t(`music_albums.${album.id}.title`, album.title)}
                  </h3>
                  <p className="font-sans text-xs text-[#3D2B1F] leading-snug mb-1 line-clamp-2">
                    {t(`music_albums.${album.id}.description`, album.description)}
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-auto pt-1">
                    <div>
                      <span className="font-sans text-lg font-bold text-[#8B6F5C]">${album.price}</span>
                      <span className="font-sans text-xs text-[#5C4A3A] ml-1">{t('common.usd')}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => setActiveAlbum(album)}
                        className="w-9 h-9 flex items-center justify-center border border-sand/60 rounded-full text-[#5C4A3A] hover:border-terracotta hover:text-terracotta transition-colors duration-300"
                        title={t('common.escuchar_muestra')}
                      >
                        <i className="ri-play-fill text-sm" />
                      </button>
                      <Link
                        to={album.id === 1 ? "/album-nanas-del-utero" : album.id === 2 ? "/album-luna-de-miel" : album.id === 3 ? "/album-ondas-de-calma" : album.id === 4 ? "/album-frecuencias-del-cerebro" : album.id === 5 ? "/album-despertar-suave" : "/album-ritual-nocturno"}
                        className="inline-flex items-center gap-1.5 bg-terracotta text-white px-4 py-2 rounded-full font-sans text-xs font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap"
                      >
                        <i className="ri-time-line" />
                        {t('common.proximamente')}
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl overflow-hidden border border-sand/40 mb-12 md:mb-16 flex flex-col sm:flex-row">
            <div className="w-full h-auto sm:w-[170px] sm:h-[170px] flex-shrink-0 overflow-hidden">
              <img
                src={i18n.language === 'en' ? bundleImages.en : bundleImages.es}
                alt={t('home.music.bundle_title')}
                className="w-full h-auto object-contain sm:h-full sm:object-cover"
              />
            </div>
            <div className="p-4 flex flex-col sm:flex-row flex-grow items-center gap-3 sm:gap-4 text-center sm:text-left">
              <div className="flex-1 min-w-0 w-full">
                <span className="inline-block font-sans text-[11px] font-semibold text-terracotta uppercase tracking-[0.2em] mb-1">
                  {t('common.oferta_especial')}
                </span>
                <h3 className="font-serif text-lg font-bold text-[#8B6F5C] mb-1 leading-tight">
                  {t('home.music.bundle_title')}
                </h3>
                <p className="font-sans text-sm text-[#3D2B1F] mb-1 leading-snug">
                  {t('home.music.bundle_save')} ${savings.toFixed(2)}. {t('home.music.bundle_desc')}
                </p>
                <p className="font-sans text-xs text-[#5C4A3A]">
                  {t('home.music.bundle_original')}: ${totalPrice.toFixed(2)} {t('common.usd')} · {t('home.music.bundle_total')}
                </p>
              </div>
              <Link
                to="/coleccion-completa-musica"
                className="inline-flex items-center justify-center self-center sm:self-auto bg-terracotta text-white px-5 py-2.5 rounded-full font-sans text-sm font-semibold hover:bg-brown transition-colors duration-300 whitespace-nowrap flex-shrink-0"
              >
                {t('home.music.bundle_cta')}
                <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>
          </div>

          <div className="bg-dark-brown rounded-2xl p-8 md:p-12">
            <div className="w-8 h-8 flex items-center justify-center text-cream mb-4">
              <i className="ri-heart-3-line text-2xl" />
            </div>
            <p className="font-sans text-base text-cream leading-relaxed max-w-3xl">
              {t('home.music.cierre')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}